"use client";
import { useEffect, useMemo, useState } from "react";
import { Building2, Check } from "lucide-react";
import { TENANT_ID, PROJECTS } from "@/app/lib/enums";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./select";
import { Label } from "./label";
import { Button } from "./button";
import { CustomTooltip } from "./tooltip";
import {
   DropdownMenu,
   DropdownMenuContent,
   DropdownMenuItem,
   DropdownMenuLabel,
   DropdownMenuSeparator,
   DropdownMenuTrigger
} from "./dropdown-menu";
import { cn } from "@/app/lib/utils";
import { useAutocompleteTenantStore } from "@/app/store/autocomplete-tenant";

type ProjectKey = keyof typeof PROJECTS;

export type TenantOption = {
   id: number;
   name: string;
   value: PROJECTS[];
};

const createTenantOptions = (): TenantOption[] => {
   const projectOptions = Object.keys(PROJECTS)
      .filter((project): project is ProjectKey => project in PROJECTS)
      .map((project) => ({
         id: TENANT_ID[project],
         name: PROJECTS[project],
         value: [PROJECTS[project]] as PROJECTS[]
      }));

   return [
      {
         id: 0,
         name: "All",
         value: Object.keys(PROJECTS).map((project) => PROJECTS[project as ProjectKey])
      },
      ...projectOptions
   ];
};

export const tenantOptions: TenantOption[] = createTenantOptions();

export const getTenantOptionById = (tenantId: number): TenantOption =>
   tenantOptions.find((tenant) => tenant.id === tenantId) || tenantOptions[0];

type AutocompleteTenantProps = {
   value?: number;
   onValueChange?: (value: TenantOption) => void;
   disabled?: boolean;
   className?: string;
   label?: string;
   hiddenLabel?: boolean;
   allowAll?: boolean;
   compact?: boolean;
};
export const AutocompleteTenant = ({
   value,
   onValueChange,
   disabled = false,
   className,
   label = "Select a tenant",
   hiddenLabel = false,
   allowAll = true,
   compact = false
}: AutocompleteTenantProps) => {
   const { selectedTenantId, setSelectedTenantId, hasHydrated } =
      useAutocompleteTenantStore();

   const [selectedTenant, setSelectedTenant] = useState<TenantOption>(tenantOptions[0]);
   const [isDropdownOpen, setIsDropdownOpen] = useState(false);
   const selectableTenantOptions = useMemo(
      () =>
         allowAll ? tenantOptions : tenantOptions.filter((tenant) => tenant.id !== 0),
      [allowAll]
   );

   useEffect(() => {
      if (!hasHydrated) {
         return;
      }

      const cachedTenant = getTenantOptionById(selectedTenantId);
      const tenant =
         allowAll || cachedTenant.id !== 0
            ? cachedTenant
            : selectableTenantOptions.find((option) => option.id === value) ||
              selectableTenantOptions[0];
      setSelectedTenant(tenant);

      if (selectedTenantId !== tenant.id) {
         setSelectedTenantId(tenant.id);
      }

      // If the parent passed a value but it's out of sync with our cached state,
      // we force the parent to update its state to match the cache.
      if (value !== undefined && value !== selectedTenantId) {
         onValueChange?.(tenant);
      }
   }, [
      allowAll,
      hasHydrated,
      onValueChange,
      selectableTenantOptions,
      selectedTenantId,
      setSelectedTenantId,
      value
   ]);

   const handleValueChange = (val: string) => {
      const newTenantId = Number(val);
      const tenant = selectableTenantOptions.find((option) => option.id === newTenantId);
      if (tenant) {
         setSelectedTenantId(newTenantId);
         setSelectedTenant(tenant);
         onValueChange?.(tenant);
      }
   };

   if (!hasHydrated) {
      return null;
   }

   if (compact) {
      return (
         <DropdownMenu open={isDropdownOpen} onOpenChange={setIsDropdownOpen}>
            <CustomTooltip
               text={isDropdownOpen ? undefined : `${label}: ${selectedTenant.name}`}
               position="right"
               delayDuration={150}
            >
               <DropdownMenuTrigger asChild>
                  <Button
                     variant="outline"
                     size="icon"
                     disabled={disabled}
                     className={cn(
                        "h-10 w-10 rounded-lg border-input bg-background hover:bg-muted focus-visible:ring-2 focus-visible:ring-ring",
                        className
                     )}
                     aria-label={`${label}: ${selectedTenant.name}`}
                     aria-expanded={isDropdownOpen}
                     aria-haspopup="menu"
                  >
                     <Building2 className="h-4 w-4 shrink-0 text-muted-foreground" />
                  </Button>
               </DropdownMenuTrigger>
            </CustomTooltip>
            <DropdownMenuContent side="right" align="start" className="w-52 z-50">
               <DropdownMenuLabel>{label}</DropdownMenuLabel>
               <DropdownMenuSeparator />
               {selectableTenantOptions.map((tenant) => (
                  <DropdownMenuItem
                     key={tenant.id}
                     onSelect={() => handleValueChange(tenant.id.toString())}
                     className="flex items-center justify-between cursor-pointer"
                  >
                     <span
                        className={cn(
                           tenant.id === selectedTenant.id && "font-bold text-primary"
                        )}
                     >
                        {tenant.name}
                     </span>
                     {tenant.id === selectedTenant.id && (
                        <Check className="h-4 w-4 text-primary" />
                     )}
                  </DropdownMenuItem>
               ))}
            </DropdownMenuContent>
         </DropdownMenu>
      );
   }

   return (
      <div className="space-y-2">
         {!hiddenLabel && <Label>{label}</Label>}
         <Select
            value={selectedTenant.id.toString()}
            onValueChange={handleValueChange}
            disabled={disabled}
         >
            <SelectTrigger className={className}>
               <SelectValue placeholder="Select a tenant" />
            </SelectTrigger>
            <SelectContent className={className}>
               {selectableTenantOptions.map((tenant) => (
                  <SelectItem
                     key={tenant.id}
                     value={tenant.id.toString()}
                     className={className}
                  >
                     {tenant.name}
                  </SelectItem>
               ))}
            </SelectContent>
         </Select>
      </div>
   );
};
